import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Cpu, BarChart3 } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

const AIModelControllerCard = () => {
  const t = useLang();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newModel, setNewModel] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/ai-model`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching AI model config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = (model) => {
    setNewModel(model);
    setShowModal(true);
  };

  const confirmModelChange = async () => {
    setSaving(true);
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/ai-model`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ active_model: newModel })
        }
      );

      if (response.ok) {
        alert(`✅ ${t('admin.aiModel.modelUpdated')}\n\n${t('admin.audit.logged')}`);
        fetchConfig();
        setShowModal(false);
      }
    } catch (error) {
      alert(`❌ ${t('admin.error.updateFailed')}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card 
        className="border-0"
        style={{
          boxShadow: EMZI_SHADOWS.card,
          borderRadius: EMZI_RADIUS.md,
        }}
      >
        <CardContent className="py-8 text-center" style={{ color: EMZI_COLORS.textSecondary }}>
          {t('common.loading')}...
        </CardContent>
      </Card>
    );
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num;
  };

  return (
    <>
      <Card 
        className="hover:shadow-lg transition-shadow border-0"
        style={{
          boxShadow: EMZI_SHADOWS.card,
          borderRadius: EMZI_RADIUS.md,
        }}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Cpu size={24} strokeWidth={2} style={{ color: EMZI_COLORS.primary }} />
            <div>
              <div style={{ color: EMZI_COLORS.primary, fontWeight: '700' }}>
                {t('admin.aiModel.title')}
              </div>
              <div className="text-xs font-normal text-gray-600 mt-1">
                {t('admin.aiModel.subtitle')}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Active Model */}
          <div 
            className="rounded-lg p-3"
            style={{
              backgroundColor: '#EBF4FF',
              border: `1px solid ${EMZI_COLORS.primary}20`,
              borderRadius: EMZI_RADIUS.sm,
            }}
          >
            <div 
              className="text-xs mb-1"
              style={{ color: EMZI_COLORS.textSecondary }}
            >
              {t('admin.aiModel.activeModel')}
            </div>
            <div 
              className="text-xl font-bold"
              style={{ color: EMZI_COLORS.primary }}
            >
              {config?.active_model?.toUpperCase() || 'N/A'}
            </div>
          </div>

          {/* Usage & Cost */}
          <div className="grid grid-cols-2 gap-2">
            <div 
              className="rounded-lg p-3"
              style={{
                backgroundColor: '#EBF4FF',
                border: `1px solid ${EMZI_COLORS.primary}20`,
                borderRadius: EMZI_RADIUS.sm,
              }}
            >
              <div 
                className="text-xs mb-1"
                style={{ color: EMZI_COLORS.textSecondary }}
              >
                {t('admin.aiModel.tokens')}
              </div>
              <div 
                className="text-lg font-bold"
                style={{ color: EMZI_COLORS.primary }}
              >
                {formatNumber(config?.current_usage?.total_tokens || 0)}
              </div>
            </div>
            <div 
              className="rounded-lg p-3"
              style={{
                backgroundColor: '#F0FDF4',
                border: `1px solid ${EMZI_COLORS.success}20`,
                borderRadius: EMZI_RADIUS.sm,
              }}
            >
              <div 
                className="text-xs mb-1"
                style={{ color: EMZI_COLORS.textSecondary }}
              >
                {t('admin.aiModel.cost')}
              </div>
              <div 
                className="text-lg font-bold"
                style={{ color: EMZI_COLORS.success }}
              >
                RM {config?.cost_data?.total_cost_myr?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>

          {/* Model Selection */}
          <div>
            <label 
              className="block text-xs font-semibold mb-2"
              style={{ color: EMZI_COLORS.textPrimary }}
            >
              {t('admin.aiModel.switchModel')}
            </label>
            <select
              value={config?.active_model || ''}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full px-3 py-2 text-sm"
              style={{
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.sm,
                color: EMZI_COLORS.textPrimary,
              }}
            >
              <option value="gpt-4">GPT-4 (Complex)</option>
              <option value="gpt-4o-mini">GPT-4o-Mini (Light)</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Actions */}
          <div className="pt-2">
            <button
              onClick={fetchConfig}
              className="w-full py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: EMZI_COLORS.primary,
                color: '#FFFFFF',
                borderRadius: EMZI_RADIUS.sm,
                minHeight: '44px',
              }}
            >
              <BarChart3 size={16} strokeWidth={2} />
              {t('admin.aiModel.viewReport')}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white shadow-2xl max-w-sm w-full p-6"
            style={{
              borderRadius: EMZI_RADIUS.md,
            }}
          >
            <h3 
              className="text-lg font-bold mb-3"
              style={{ color: EMZI_COLORS.textPrimary }}
            >
              ⚠️ {t('admin.aiModel.confirmSwitch')}
            </h3>
            <p 
              className="text-sm mb-4"
              style={{ color: EMZI_COLORS.textSecondary }}
            >
              {t('admin.aiModel.switchWarning', { model: newModel })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2"
                style={{
                  border: `2px solid ${EMZI_COLORS.border}`,
                  color: EMZI_COLORS.textSecondary,
                  borderRadius: EMZI_RADIUS.sm,
                  backgroundColor: '#FFFFFF',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmModelChange}
                disabled={saving}
                className="flex-1 px-4 py-2 disabled:opacity-50"
                style={{
                  backgroundColor: EMZI_COLORS.primary,
                  color: '#FFFFFF',
                  borderRadius: EMZI_RADIUS.sm,
                }}
              >
                {saving ? t('common.saving') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIModelControllerCard;